import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Key, Trash2, AlertCircle } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useOrganizationStore } from '@/stores/organization-store'
import { projectApiKeyService } from '@/services/project-api-key'
import { CreateApiKeyDialog } from './create-api-key-dialog'
import type { ProjectApiKey, ProjectApiKeyCreateRequest, ProjectApiKeyCreateResponse } from '@/schemas/project-api-key'
import type { AxiosError } from 'axios'

interface DialogState<T> {
  isOpen: boolean
  isProcessing: boolean
  error: string | null
  context: T | null
}

function createInitialDialogState<T>(): DialogState<T> {
  return { isOpen: false, isProcessing: false, error: null, context: null }
}

function ApiKeyCardSkeleton() {
  return (
    <Card variant="glass" className="p-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-lg bg-gray-700/50" />
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-gray-700/50 rounded w-1/3" />
            <div className="h-3 bg-gray-700/50 rounded w-1/2" />
          </div>
        </div>
        <div className="h-6 w-16 bg-gray-700/50 rounded-full" />
      </div>
    </Card>
  )
}

function formatDate(dateStr: string | null, locale: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ApiKeysTab() {
  const { t, i18n } = useTranslation()
  const { currentProject } = useOrganizationStore()
  const [apiKeys, setApiKeys] = useState<ProjectApiKey[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const [createDialog, setCreateDialog] = useState<DialogState<null> & { createdKey: ProjectApiKeyCreateResponse | null }>({
    ...createInitialDialogState<null>(),
    createdKey: null,
  })

  const [revokeDialog, setRevokeDialog] = useState<DialogState<ProjectApiKey>>(
    createInitialDialogState<ProjectApiKey>()
  )

  const fetchApiKeys = useCallback(async (projectId: string, signal?: AbortSignal) => {
    setIsLoading(true)
    setFetchError(null)
    try {
      const response = await projectApiKeyService.listApiKeys(projectId, { signal })
      setApiKeys(response.api_keys)
    } catch (error) {
      if ((error as Error).name === 'CanceledError') return
      const axiosError = error as AxiosError<{ detail: string }>
      setFetchError(axiosError.response?.data?.detail || t('settings.apiKeys.errors.fetchFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (!currentProject) return
    const controller = new AbortController()
    fetchApiKeys(currentProject.id, controller.signal)
    return () => controller.abort()
  }, [currentProject, fetchApiKeys])

  const handleCreate = async (data: ProjectApiKeyCreateRequest) => {
    if (!currentProject) return
    setCreateDialog((prev) => ({ ...prev, isProcessing: true, error: null }))
    try {
      const response = await projectApiKeyService.createApiKey(currentProject.id, data)
      setApiKeys((prev) => [
        {
          id: response.id,
          name: response.name,
          key_prefix: response.key_prefix,
          description: response.description,
          is_active: true,
          last_used_at: null,
          created_at: response.created_at,
          revoked_at: null,
        },
        ...prev,
      ])
      setCreateDialog((prev) => ({ ...prev, isProcessing: false, createdKey: response }))
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setCreateDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('settings.apiKeys.errors.createFailed'),
      }))
    }
  }

  const handleRevoke = async () => {
    if (!currentProject || !revokeDialog.context) return
    setRevokeDialog((prev) => ({ ...prev, isProcessing: true, error: null }))
    try {
      await projectApiKeyService.revokeApiKey(currentProject.id, revokeDialog.context.id)
      setApiKeys((prev) =>
        prev.map((key) =>
          key.id === revokeDialog.context!.id
            ? { ...key, is_active: false, revoked_at: new Date().toISOString() }
            : key
        )
      )
      setRevokeDialog(createInitialDialogState<ProjectApiKey>())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setRevokeDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('settings.apiKeys.errors.revokeFailed'),
      }))
    }
  }

  if (!currentProject) {
    return (
      <Card variant="glass">
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <p className="text-amber-400">{t('settings.apiKeys.noProject')}</p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-100">
            {t('settings.apiKeys.title')}
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            {t('settings.apiKeys.subtitle')}
          </p>
        </div>
        <Button
          variant="action-indigo"
          onClick={() => setCreateDialog({ isOpen: true, isProcessing: false, error: null, context: null, createdKey: null })}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('settings.apiKeys.create.button')}
        </Button>
      </div>

      {/* Usage hint */}
      <Card variant="glass" className="p-4">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-indigo-400 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-400">
            <p>{t('settings.apiKeys.usageHint')}</p>
            <code className="mt-2 block p-2 rounded bg-gray-800/50 text-gray-300 font-mono text-xs">
              Authorization: Bearer sk_...
            </code>
          </div>
        </div>
      </Card>

      {/* Error */}
      {fetchError && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-400">{fetchError}</p>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <ApiKeyCardSkeleton key={i} />)
        ) : apiKeys.length === 0 ? (
          <Card variant="glass">
            <div className="p-8 text-center">
              <Key className="w-12 h-12 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400">{t('settings.apiKeys.empty')}</p>
            </div>
          </Card>
        ) : (
          apiKeys.map((apiKey) => (
            <Card key={apiKey.id} variant="glass" className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0">
                    <Key className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-200 truncate">{apiKey.name}</h4>
                      {apiKey.is_active ? (
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full shrink-0">
                          {t('settings.apiKeys.status.active')}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded-full shrink-0">
                          {t('settings.apiKeys.status.revoked')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <code className="font-mono">{apiKey.key_prefix}...</code>
                      <span>{t('settings.apiKeys.createdAt')}: {formatDate(apiKey.created_at, i18n.language)}</span>
                      {apiKey.last_used_at && (
                        <span>{t('settings.apiKeys.lastUsed')}: {formatDate(apiKey.last_used_at, i18n.language)}</span>
                      )}
                    </div>
                    {apiKey.description && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{apiKey.description}</p>
                    )}
                  </div>
                </div>

                {apiKey.is_active && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                    onClick={() => setRevokeDialog({ isOpen: true, isProcessing: false, error: null, context: apiKey })}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <CreateApiKeyDialog
        open={createDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialog({ isOpen: false, isProcessing: false, error: null, context: null, createdKey: null })
          }
        }}
        isProcessing={createDialog.isProcessing}
        error={createDialog.error}
        createdKey={createDialog.createdKey}
        onSubmit={handleCreate}
      />

      {/* Revoke Confirmation */}
      <ConfirmationDialog
        open={revokeDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) setRevokeDialog(createInitialDialogState<ProjectApiKey>())
        }}
        title={t('settings.apiKeys.revoke.title')}
        description={
          <>
            {t('settings.apiKeys.revoke.description')}
            {revokeDialog.context && (
              <span className="block mt-2 font-mono text-gray-300">
                {revokeDialog.context.name} ({revokeDialog.context.key_prefix}...)
              </span>
            )}
          </>
        }
        confirmLabel={t('settings.apiKeys.revoke.confirm')}
        confirmingLabel={t('settings.apiKeys.revoke.confirming')}
        confirmVariant="action-red"
        isProcessing={revokeDialog.isProcessing}
        error={revokeDialog.error}
        onConfirm={handleRevoke}
      />
    </div>
  )
}
